import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Switch, TextField, InputAdornment, CircularProgress, Alert } from '@mui/material';
import { BiSolidBus, BiPhone } from 'react-icons/bi';
import { PiCarFill, PiPackageFill, PiWrenchFill } from 'react-icons/pi';
import { formatPhone } from '@services/formatService';
import { useGetDriverCustomerConfigsQuery } from '../../../drivers/queries/getDriverCustomerConfigs.query.generated';
import { useUser } from '@store';
import { enrichCustomerConfig } from '@utils/configsService';
import { getEnabledProductsFromConfigs, ATTRIBUTE_META, type MobilityAttribute } from '@utils/productMapper';

// -- Types --

export type DriverAttributeState = Partial<Record<MobilityAttribute, boolean>>;

interface DriverAttributesStepProps {
  attributes: DriverAttributeState;
  onChange: (attributes: DriverAttributeState) => void;
  selectedCustomerIds: number[];
  phone: string;
  onPhoneChange: (phone: string) => void;
  phoneError?: string;
}

// -- Icon mapping --

const ATTRIBUTE_ICONS: Record<MobilityAttribute, React.ComponentType<{ size: number }>> = {
  mobility_valet: PiCarFill,
  mobility_shuttle: BiSolidBus,
  mobility_service: PiWrenchFill,
  mobility_parts: PiPackageFill,
};

// -- Component --

const DriverAttributesStep: React.FC<DriverAttributesStepProps> = ({
  attributes,
  onChange,
  selectedCustomerIds,
  phone,
  onPhoneChange,
  phoneError,
}) => {
  const [phoneFocused, setPhoneFocused] = useState(false);
  const user = useUser();
  const userId = user?.profile?.id;

  const [enabledProducts, setEnabledProducts] = useState<Set<MobilityAttribute>>(new Set());
  const [loading, setLoading] = useState(true);
  const enrichedForRef = useRef<string | null>(null);

  // Query customer configs for the current admin user
  const { data, loading: queryLoading } = useGetDriverCustomerConfigsQuery({
    variables: { userId: userId! },
    skip: !userId,
    fetchPolicy: 'cache-first',
  });

  // Enrich configs and derive enabled products for selected customers only
  useEffect(() => {
    if (queryLoading || !data?.usertocustomers) return;

    const selectedSet = new Set(selectedCustomerIds);
    const relevantCustomers = data.usertocustomers.filter(utc => selectedSet.has(utc.customer_id));

    const cacheKey = relevantCustomers
      .map(c => c.customer_id)
      .sort()
      .join(',');
    if (enrichedForRef.current === cacheKey) return;

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      try {
        const token = await user?.getToken();
        if (!token || cancelled) return;

        const enrichedConfigs = await Promise.all(
          relevantCustomers.map(utc =>
            enrichCustomerConfig(token, utc.customer.config, utc.customer.organization?.config).catch(() => null)
          )
        );

        if (cancelled) return;

        // Move-type products (lite rooftops only) + service-flag products
        // (e.g. Mobile Service)
        setEnabledProducts(
          getEnabledProductsFromConfigs(
            enrichedConfigs.map((cfg, i) => ({
              // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
              tier: relevantCustomers[i].customer.tier,
              enrichedConfig: cfg,
            }))
          )
        );
        enrichedForRef.current = cacheKey;
      } catch (err) {
        console.error('Error resolving driver products:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [data, queryLoading, user, selectedCustomerIds]);

  // When enabled products change, remove unavailable attributes and auto-enable sole attribute
  useEffect(() => {
    if (loading) return;
    const cleaned: DriverAttributeState = {};
    let changed = false;
    for (const [key, val] of Object.entries(attributes)) {
      if (enabledProducts.has(key as MobilityAttribute)) {
        cleaned[key as MobilityAttribute] = val;
      } else {
        changed = true;
      }
    }
    // Auto-enable when there's exactly one available attribute
    if (enabledProducts.size === 1) {
      const onlyKey = [...enabledProducts][0];
      if (!cleaned[onlyKey]) {
        cleaned[onlyKey] = true;
        changed = true;
      }
    }
    if (changed) onChange(cleaned);
  }, [enabledProducts, loading]);

  const visibleAttributes = useMemo(
    () =>
      [...enabledProducts].map(key => ({
        ...ATTRIBUTE_META[key],
        icon: ATTRIBUTE_ICONS[key],
      })),
    [enabledProducts]
  );

  const handleToggle = (key: MobilityAttribute, checked: boolean) => {
    onChange({ ...attributes, [key]: checked });
  };

  const phoneDisplay = !phoneFocused && phone ? (formatPhone(phone) ?? phone) : phone;

  return (
    <div>
      <Typography variant='overline' fontWeight={600} color='text.secondary' sx={{ mb: 2, display: 'block' }}>
        Driver Setup
      </Typography>

      {/* Phone number — required for rideshare notifications */}
      <Box sx={{ mb: 3 }}>
        <Typography variant='body2' sx={{ fontWeight: 500, mb: 0.5 }}>
          Driver Phone Number
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Our platform uses this number to book rideshare trips (Uber/Lyft) on the driver's behalf. Without a valid
          mobile number, rides cannot be ordered for them.
        </Typography>
        <TextField
          placeholder='e.g. (555) 123-4567'
          size='small'
          fullWidth
          value={phoneDisplay}
          onChange={e => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
            onPhoneChange(digits);
          }}
          onFocus={() => setPhoneFocused(true)}
          onBlur={() => setPhoneFocused(false)}
          error={!!phoneError}
          helperText={phoneError}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <BiPhone size={20} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Select which types of moves this driver can perform.
      </Typography>

      {(loading || queryLoading) && (
        <Box display='flex' justifyContent='center' p={3}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && !queryLoading && visibleAttributes.length === 0 && (
        <Alert severity='info'>No products are enabled across the selected rooftops.</Alert>
      )}

      {!loading &&
        !queryLoading &&
        visibleAttributes.map(attr => {
          const IconComponent = attr.icon;
          const isOn = !!attributes[attr.key];

          return (
            <Box
              key={attr.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 1,
                borderRadius: 1,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
              }}
            >
              <Box display='flex' alignItems='center' gap={1.5}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isOn ? '#eff6ff' : '#f4f4f5',
                    color: isOn ? '#2563eb' : '#9ca3af',
                    transition: 'all 150ms',
                  }}
                >
                  <IconComponent size={20} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{attr.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{attr.description}</Typography>
                </Box>
              </Box>

              <Switch
                checked={isOn}
                onChange={(_, checked) => handleToggle(attr.key, checked)}
                disabled={visibleAttributes.length === 1}
                size='small'
              />
            </Box>
          );
        })}
    </div>
  );
};

export default DriverAttributesStep;
